import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon, ArrowDownTrayIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import { useCurrency } from '../hooks/useCurrency';

interface PMVPMetric {
  label: string;
  value: number | string;
  target?: number | string;
  status: 'real' | 'target' | 'partial';
  description: string;
}

interface ReportRow {
  label: string;
  value: string | number;
}

const FundingReadinessPage: React.FC = () => {
  const { format: formatCurrency } = useCurrency();
  const [metrics, setMetrics] = useState<PMVPMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  const token = localStorage.getItem('aaywa_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const [adminRes, traceRes, donationRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/executive-summary`, { headers }),
        fetch(`${API_URL}/api/traceability/summary`, { headers }),
        fetch(`${API_URL}/api/donations/summary`, { headers }).catch(() => null),
      ]);

      const admin = adminRes.ok ? await adminRes.json() : null;
      const trace = traceRes.ok ? await traceRes.json() : null;
      const donation = donationRes && donationRes.ok ? await donationRes.json() : null;

      const built: PMVPMetric[] = [
        {
          label: 'Registered Farmers',
          value: admin?.programPerformance?.totalFarmers ?? 0,
          target: 100,
          status: 'real',
          description: 'Farmers registered in the platform',
        },
        {
          label: 'Active Cohorts',
          value: admin?.programPerformance?.activeCohorts ?? admin?.programPerformance?.totalCohorts ?? 0,
          status: 'real',
          description: 'Active cohort groups in the system',
        },
        {
          label: 'Mapped Farm Plots',
          value: admin?.programPerformance?.mappedPlots ?? 0,
          status: 'real',
          description: 'Farm plots with GPS boundaries recorded',
        },
        {
          label: 'Traceable Trees/Crops',
          value: trace?.summary?.total_trees ?? 0,
          status: 'real',
          description: 'Individual trees or crop assets tracked',
        },
        {
          label: 'Mapped Trees (GPS)',
          value: trace?.summary?.mapped_trees ?? 0,
          status: 'real',
          description: 'Trees with confirmed GPS coordinates',
        },
        {
          label: 'Harvest Lots',
          value: trace?.summary?.harvest_lots ?? 0,
          status: 'real',
          description: 'Harvest lots linked to traceability records',
        },
        {
          label: 'Public Certificates',
          value: trace?.summary?.public_certificates ?? 0,
          status: 'real',
          description: 'QR-linked public traceability certificates issued',
        },
        {
          label: 'Training Sessions',
          value: admin?.programPerformance?.trainingCompletionRate ?? 0,
          status: 'real',
          description: 'Training completion rate (%)',
        },
        {
          label: 'VSLA Total Savings',
          value: admin?.financialOverview?.vslaFundTotal
            ? formatCurrency(Number(admin.financialOverview.vslaFundTotal))
            : formatCurrency(0),
          status: 'real',
          description: 'Total savings across all VSLA groups',
        },
        {
          label: 'Donations Raised',
          value: donation?.totals?.total_raised
            ? `$${Number(donation.totals.total_raised).toFixed(0)}`
            : '$0',
          status: 'real',
          description: 'Confirmed donations received via AAYWA',
        },
        {
          label: 'Open Field Issues',
          value: trace?.summary?.open_risks ?? 0,
          status: 'real',
          description: 'Trees/crops flagged with active issues',
        },
        {
          label: 'Staff Users',
          value: admin?.programPerformance?.totalStaff ?? 0,
          status: 'real',
          description: 'Active staff accounts on the platform',
        },
      ];

      setMetrics(built);
    } catch (err) {
      console.error('Failed to load metrics', err);
    }
    setLoading(false);
  };

  const loadReport = async (type: string) => {
    setActiveReport(type);
    setReportLoading(true);
    setReportData([]);

    try {
      let url = '';
      if (type === 'cohort') url = `${API_URL}/api/admin/analytics/by-cohort`;
      else if (type === 'crop') url = `${API_URL}/api/admin/analytics/by-crop`;
      else if (type === 'period') url = `${API_URL}/api/admin/analytics/by-period`;

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        if (rows.length > 0) {
          setReportData(
            rows.map((r: any) => ({
              label: r.cohort_name || r.crop_type || r.period || 'Unknown',
              value: r.total_revenue ?? r.total_quantity_kg ?? r.farmer_count ?? '',
            }))
          );
        }
      }
    } catch (err) {
      console.error('Report load error', err);
    }
    setReportLoading(false);
  };

  const exportMetricsCSV = () => {
    const rows = [
      ['Metric', 'Value', 'Status', 'Description'],
      ...metrics.map(m => [m.label, String(m.value), m.status, m.description]),
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pmvp-evidence-${Date.now()}.csv`;
    a.click();
  };

  const statusBadge = (status: PMVPMetric['status']) => {
    if (status === 'real') return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">REAL DATA</span>;
    if (status === 'target') return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">TARGET</span>;
    return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full">PARTIAL</span>;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="bg-emerald-100 p-3 rounded-xl">
            <ChartBarIcon className="w-8 h-8 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Funding Readiness</h1>
            <p className="text-gray-500 mt-0.5">PMVP evidence dashboard — real metrics only, no fake numbers</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchMetrics} className="p-2.5 border-2 border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
          <button onClick={exportMetricsCSV}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700">
            <ArrowDownTrayIcon className="w-5 h-5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Integrity notice */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 flex gap-3">
        <CheckCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-blue-900">Data Integrity Policy</p>
          <p className="text-sm text-blue-700 mt-0.5">
            All metrics shown are pulled live from the database. Targets are labelled clearly.
            No hardcoded or fake numbers are used in this dashboard.
          </p>
        </div>
      </div>

      {/* Metrics Grid */}
      {loading ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-emerald-500 mx-auto" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {metrics.map((m) => (
            <div key={m.label} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide leading-tight">{m.label}</p>
                {statusBadge(m.status)}
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{m.value}</p>
              {m.target && (
                <p className="text-xs text-gray-400">Target: {m.target}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">{m.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reports Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="font-bold text-gray-900">Quick Reports</h2>
          <p className="text-sm text-gray-500 mt-0.5">Export-ready summaries for funder presentations</p>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { key: 'cohort', label: 'By Cohort' },
              { key: 'crop', label: 'By Crop' },
              { key: 'period', label: 'By Period' },
            ].map((r) => (
              <button key={r.key} onClick={() => loadReport(r.key)}
                className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${activeReport === r.key
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {r.label}
              </button>
            ))}
          </div>

          {reportLoading && (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-emerald-500 mx-auto" />
            </div>
          )}

          {!reportLoading && reportData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-semibold text-gray-900">{row.label}</td>
                      <td className="px-6 py-3 text-sm text-gray-600">{String(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!reportLoading && activeReport && reportData.length === 0 && (
            <div className="p-8 text-center text-gray-400">No data available for this report yet.</div>
          )}
        </div>
      </div>

      {/* Pitch Checklist */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="font-bold text-gray-900">Pitch Readiness Checklist</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            { item: 'eSanza and AAYWA are clearly separated', done: true },
            { item: 'Dashboard login and permissions are secure', done: true },
            { item: 'Super admin can govern users and settings', done: true },
            { item: 'Website content can be updated from dashboard', done: true },
            { item: 'AAYWA donation campaigns are configured', done: false },
            { item: 'Traceability tree batches stay inside field boundaries', done: true },
            { item: 'Map is fast and usable', done: true },
            { item: 'Mobile field staff can update tree observations', done: true },
            { item: 'Issues appear on dashboard maps', done: true },
            { item: 'Public pages contain no fake claims', done: true },
            { item: 'Reports can be exported as CSV', done: true },
            { item: 'Audit logs track all sensitive actions', done: true },
          ].map((c, i) => (
            <div key={i} className="px-6 py-4 flex items-center gap-4">
              {c.done
                ? <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                : <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />}
              <span className={`text-sm ${c.done ? 'text-gray-700' : 'text-yellow-700 font-semibold'}`}>{c.item}</span>
              <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${c.done ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {c.done ? 'Done' : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FundingReadinessPage;
